import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Change, Loader, Empty, RetryState } from "@/components/widgets";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MagnifyingGlass, CurrencyBtc, CaretUp, CaretDown, BellSimple, Fire, Bank } from "@phosphor-icons/react";
import { toast } from "sonner";
import CryptoSignalsPanel from "@/components/CryptoSignalsPanel";

const GRID = "grid-cols-[120px_1fr_110px_84px_112px_56px_80px_40px]";

const scoreColor = (s) => s == null ? "text-muted-foreground" : s >= 60 ? "text-signal-green" : s <= 40 ? "text-signal-red" : "text-signal-yellow";
const dirColor = (d) => d === "bullish" ? "text-signal-green" : d === "bearish" ? "text-signal-red" : "text-muted-foreground";
const label = (t) => t.replace("-USD", "");
const fmtCap = (v) => {
  if (v == null) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return `$${v.toLocaleString()}`;
};

const ALERT_TYPES = [
  ["price_above", "Price rises above"],
  ["price_below", "Price falls below"],
  ["score_above", "AI Score rises above"],
  ["score_below", "AI Score falls below"],
];

export default function Crypto() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ col: "score", dir: "desc" });
  const [minScore, setMinScore] = useState("0");
  const [signal, setSignal] = useState("any");
  const [move, setMove] = useState("any");
  const [err, setErr] = useState(false);

  const load = () => { setErr(false); api.get("/crypto").then((r) => setData(r.data)).catch(() => setErr(true)); };
  useEffect(() => { load(); }, []);

  // Cold pod: prices "warming up" — poll until they arrive instead of hard-failing.
  useEffect(() => {
    if (!data?.warming) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [data?.warming]);

  const setSortCol = (col) => setSort((s) => s.col === col
    ? { col, dir: s.dir === "asc" ? "desc" : "asc" }
    : { col, dir: col === "ticker" || col === "name" ? "asc" : "desc" });

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toUpperCase();
    const ms = Number(minScore);
    const f = data.coins.filter((c) => {
      if (term && !c.ticker.includes(term) && !c.name.toUpperCase().includes(term)) return false;
      if (ms > 0 && (c.score == null || c.score < ms)) return false;
      if (signal !== "any" && c.direction !== signal) return false;
      if (move === "gainers" && !(c.change_pct > 0)) return false;
      if (move === "losers" && !(c.change_pct < 0)) return false;
      if (move === "movers" && !(Math.abs(c.change_pct || 0) >= 5)) return false;
      return true;
    });
    const { col, dir } = sort;
    return [...f].sort((a, b) => {
      let av = a[col], bv = b[col];
      if (col === "ticker" || col === "name") return dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return dir === "asc" ? av - bv : bv - av;
    });
  }, [data, q, sort, minScore, signal, move]);

  const trending = useMemo(() => {
    if (!data) return [];
    return [...data.coins]
      .filter((c) => c.change_pct != null && c.price != null)
      .sort((a, b) => Math.abs(b.change_pct) - Math.abs(a.change_pct))
      .slice(0, 8);
  }, [data]);

  // Quick 24/7 alert
  const [alertCoin, setAlertCoin] = useState(null);
  const [aType, setAType] = useState("price_below");
  const [aThreshold, setAThreshold] = useState("");
  const [aBusy, setABusy] = useState(false);
  const openAlert = (c) => { setAlertCoin(c); setAType(c.direction === "bearish" ? "price_below" : "price_above"); setAThreshold(c.price != null ? String(c.price) : ""); };
  const createAlert = async () => {
    if (!(Number(aThreshold) > 0)) { toast.error("Enter a valid threshold"); return; }
    setABusy(true);
    try {
      await api.post("/alerts", { ticker: alertCoin.ticker, alert_type: aType, threshold: Number(aThreshold) });
      toast.success(`24/7 alert set on ${label(alertCoin.ticker)}`, { description: "Fires whenever the condition is met — evaluate on the Alerts page." });
      setAlertCoin(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setABusy(false); }
  };

  if (!data) return err
    ? <RetryState label="Couldn't load crypto — the market data feed may be busy." onRetry={load} />
    : <Loader label="Loading crypto universe" />;

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="label-xs">Market Data Layer</div>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter flex items-center gap-2"><CurrencyBtc size={26} weight="fill" className="text-signal-yellow" /> Crypto</h1>
        </div>
        <span className="mono text-xs text-muted-foreground">{data.count} coins · 24/7 · real spot prices</span>
      </div>

      {data.warming && (
        <div data-testid="crypto-warming" className="mt-4 flex items-center gap-2 border border-signal-yellow/30 bg-signal-yellow/[0.06] rounded-sm px-3 py-2 text-xs text-muted-foreground">
          <span className="h-2 w-2 rounded-full bg-signal-yellow animate-pulse" />
          Live prices are warming up from the market feed — refreshing automatically…
        </div>
      )}

      {/* Trending / hot movers strip */}
      {trending.length > 0 && (
        <div className="mt-5" data-testid="trending-strip">
          <div className="label-xs flex items-center gap-1.5 mb-2 text-signal-yellow"><Fire size={14} weight="fill" /> Hot Right Now · biggest 24h movers</div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {trending.map((c) => (
              <button key={c.ticker} data-testid={`trending-${label(c.ticker)}`} onClick={() => nav(`/stock/${c.ticker}`)}
                className="shrink-0 w-[150px] border border-border bg-card rounded-sm p-3 text-left hover:border-volt/40 transition-colors duration-200">
                <div className="flex items-center gap-2">
                  <CoinLogo ticker={c.ticker} logo={c.logo} />
                  <span className="mono font-medium text-sm">{label(c.ticker)}</span>
                  {c.alpaca_tradable && <Bank size={11} weight="fill" className="text-volt shrink-0" title="Tradable on your Alpaca broker" />}
                </div>
                <div className="mono text-sm mt-2">${c.price.toLocaleString(undefined, { maximumFractionDigits: 6 })}</div>
                <div className="mt-0.5"><Change value={c.change_pct} className="text-xs" /></div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Scanner filters */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="crypto-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search coin (BTC, Ethereum…)" className="pl-9 bg-card rounded-sm h-9" />
        </div>
        <Select value={minScore} onValueChange={setMinScore}>
          <SelectTrigger data-testid="crypto-filter-score" className="bg-card rounded-sm h-9 w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Any AI score</SelectItem>
            <SelectItem value="60">Score 60+</SelectItem>
            <SelectItem value="70">Score 70+</SelectItem>
            <SelectItem value="80">Score 80+</SelectItem>
          </SelectContent>
        </Select>
        <Select value={signal} onValueChange={setSignal}>
          <SelectTrigger data-testid="crypto-filter-signal" className="bg-card rounded-sm h-9 w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any signal</SelectItem>
            <SelectItem value="bullish">Bullish</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
            <SelectItem value="bearish">Bearish</SelectItem>
          </SelectContent>
        </Select>
        <Select value={move} onValueChange={setMove}>
          <SelectTrigger data-testid="crypto-filter-move" className="bg-card rounded-sm h-9 w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any 24h move</SelectItem>
            <SelectItem value="gainers">Gainers ▲</SelectItem>
            <SelectItem value="losers">Losers ▼</SelectItem>
            <SelectItem value="movers">Big movers ±5%</SelectItem>
          </SelectContent>
        </Select>
        <span className="mono text-xs text-muted-foreground">{rows.length} shown</span>
      </div>

      <div className="mt-4 border border-border bg-card overflow-hidden">
        <div className={`grid ${GRID} gap-2 border-b border-border px-4 py-2.5 label-xs`}>
          <SortHead label="Coin" col="ticker" sort={sort} onClick={setSortCol} />
          <SortHead label="Name" col="name" sort={sort} onClick={setSortCol} />
          <SortHead label="Price" col="price" sort={sort} onClick={setSortCol} align="right" />
          <SortHead label="24h" col="change_pct" sort={sort} onClick={setSortCol} align="right" />
          <SortHead label="Mkt Cap" col="market_cap" sort={sort} onClick={setSortCol} align="right" />
          <SortHead label="AI" col="score" sort={sort} onClick={setSortCol} align="right" />
          <span className="text-right">Signal</span>
          <span className="text-right">Alert</span>
        </div>

        {rows.length === 0 ? <Empty>No coins match these filters</Empty> : rows.map((c) => (
          <div key={c.ticker} className={`grid ${GRID} gap-2 items-center px-4 py-3 border-b border-border/50 hover:bg-white/[0.03] transition-colors duration-200`}>
            <button data-testid={`crypto-row-${label(c.ticker)}`} onClick={() => nav(`/stock/${c.ticker}`)} className="flex items-center gap-1.5 text-left">
              <CoinLogo ticker={c.ticker} logo={c.logo} />
              <span className="mono font-medium">{label(c.ticker)}</span>
              {c.alpaca_tradable && (
                <Bank data-testid={`alpaca-badge-${label(c.ticker)}`} size={12} weight="fill" className="text-volt shrink-0" title="Tradable on your Alpaca broker" />
              )}
            </button>
            <button onClick={() => nav(`/stock/${c.ticker}`)} className="text-sm truncate text-left">{c.name}</button>
            <span className="mono text-right text-sm">{c.price != null ? `$${c.price.toLocaleString(undefined, { maximumFractionDigits: 8 })}` : "—"}</span>
            <span className="text-right">{c.change_pct != null ? <Change value={c.change_pct} className="text-xs" /> : <span className="text-muted-foreground text-xs">—</span>}</span>
            <span className="mono text-right text-sm text-muted-foreground">{fmtCap(c.market_cap)}</span>
            <span className={`mono text-right text-sm font-semibold ${scoreColor(c.score)}`}>{c.score ?? "—"}</span>
            <span className={`text-right mono text-xs uppercase ${dirColor(c.direction)}`}>{c.direction || "—"}</span>
            <button data-testid={`crypto-alert-${label(c.ticker)}`} onClick={() => openAlert(c)} title="Set a 24/7 alert"
              className="flex justify-end text-muted-foreground hover:text-volt transition-colors"><BellSimple size={16} /></button>
          </div>
        ))}
      </div>
      <p className="mt-3 mono text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">Click any coin for AI intelligence, forecasts & paper trading. The bell sets a 24/7 price/score alert. <span className="inline-flex items-center gap-1">The <Bank size={11} weight="fill" className="text-volt" /> badge marks coins Elite users can route as real orders via the Alpaca Broker.</span></p>

      {/* Quick 24/7 alert dialog */}
      <Dialog open={!!alertCoin} onOpenChange={(o) => !o && setAlertCoin(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="crypto-alert-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><BellSimple size={18} weight="fill" className="text-volt" /> 24/7 Alert · {alertCoin && label(alertCoin.ticker)}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Crypto never sleeps — this alert is checked against live 24/7 prices and AI scores.</DialogDescription>
          </DialogHeader>
          {alertCoin && (
            <div className="space-y-4">
              <div className="hypo-bg border border-white/10 p-3 rounded-sm flex items-center justify-between">
                <div><div className="mono text-sm">{alertCoin.name}</div><div className="mono text-[11px] text-muted-foreground">Now ${alertCoin.price} · AI {alertCoin.score ?? "—"}</div></div>
                <span className={`mono text-xs uppercase ${dirColor(alertCoin.direction)}`}>{alertCoin.direction}</span>
              </div>
              <div>
                <div className="label-xs mb-2">Condition</div>
                <Select value={aType} onValueChange={setAType}>
                  <SelectTrigger data-testid="crypto-alert-type" className="bg-background rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALERT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-xs mb-2">Threshold {aType.startsWith("score") ? "(0–100)" : "($)"}</div>
                <Input data-testid="crypto-alert-threshold" type="number" step="any" value={aThreshold} onChange={(e) => setAThreshold(e.target.value)} className="bg-background rounded-sm mono" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertCoin(null)} className="rounded-sm border-border">Cancel</Button>
            <Button data-testid="crypto-alert-confirm" disabled={aBusy} onClick={createAlert} className="bg-volt hover:bg-volt/90 text-white rounded-sm">{aBusy ? "Setting…" : "Set Alert"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CryptoSignalsPanel />
    </div>
  );
}

function CoinLogo({ ticker, logo }) {
  const [err, setErr] = useState(false);
  const sym = ticker.replace("-USD", "").toLowerCase();
  const src = logo || `https://raw.githubusercontent.com/spothq/cryptocurrency-icons/master/128/color/${sym}.png`;
  if (err) return <span className="h-5 w-5 rounded-full bg-white/10 shrink-0 flex items-center justify-center text-[8px] mono text-muted-foreground">{sym.slice(0, 2).toUpperCase()}</span>;
  return <img src={src} alt="" onError={() => setErr(true)} className="h-5 w-5 rounded-full shrink-0" loading="lazy" />;
}

function SortHead({ label, col, sort, onClick, align = "left" }) {
  const active = sort.col === col;
  return (
    <button data-testid={`crypto-sort-${col}`} onClick={() => onClick(col)}
      className={`flex items-center gap-1 hover:text-white transition-colors duration-200 ${align === "right" ? "justify-end" : ""} ${active ? "text-volt" : ""}`}>
      {label}
      {active && (sort.dir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)}
    </button>
  );
}
